import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Icon } from '../components/Layout'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import {
  getComments,
  createComment as apiCreateComment,
  createReply as apiCreateReply,
  updateComment as apiUpdateComment,
  deleteComment as apiDeleteComment,
  normalizeComment,
  likeComment as apiLikeComment,
} from '../services/comments'
import { adminDeleteComment } from '../services/admin'
import { renderTextContent } from '../utils/renderContent'
import { humanizeApiError } from '../services/client'

const EMOJI_TABS = [
  { label: '😀', emojis: ['😀','😂','🤣','😅','😊','😍','🥰','😎','🤩','🥳','😭','😤','🤔','🤯','😱','🥺','😴','🤑','😈','🫡','🫠','😬','🙃','😏','😒'] },
  { label: '❤️', emojis: ['❤️','🧡','💛','💚','💙','💜','🖤','🤍','💔','❣️','💕','💞','💓','💗','💖','💝','💘','💟','🫀','♥️','❤️‍🔥','❤️‍🩹','🩷','🩵','🩶'] },
  { label: '🎉', emojis: ['🎉','🎊','🎈','🎁','🏆','🥇','🎯','🎮','🕹️','🎲','🧩','🎭','🎨','🎬','🎤','🎧','🎵','🎶','🎻','🥁','🪗','🎷','🎸','🎺','🪘'] },
  { label: '💰', emojis: ['💰','💵','💴','💶','💷','💸','💳','🤑','💹','📈','📉','🏦','💎','🪙','₿','🔑','🗝️','🏧','💱','🪂','🚀','🌕','📊','🔐','🛡️'] },
  { label: '🔥', emojis: ['🔥','⚡','✨','💥','🌟','⭐','🌈','🌊','🌀','❄️','🌙','☀️','🌍','🚀','💫','🌌','🪐','☄️','🔮','🧲','⚗️','🌋','🌪️','🌩️','🫧'] },
]

const updateCommentTree = (items, id, updater) =>
  items.map((item) => {
    if (item.id === id) return updater(item)
    if (item.replies?.length) {
      return {
        ...item,
        replies: updateCommentTree(item.replies, id, updater),
      }
    }
    return item
  })

const removeCommentFromTree = (items, id) =>
  items
    .filter((item) => item.id !== id)
    .map((item) => ({
      ...item,
      replies: item.replies?.length
        ? removeCommentFromTree(item.replies, id)
        : item.replies ?? [],
    }))

function EmojiPicker({ textareaRef, onClose }) {
  const [activeTab, setActiveTab] = useState(0)
  const pickerRef = useRef(null)

  useEffect(() => {
    function handleClick(e) {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) onClose()
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [onClose])

  const insertEmoji = (emoji) => {
    const ta = textareaRef.current
    if (!ta) return
    const start = ta.selectionStart ?? ta.value.length
    const end   = ta.selectionEnd   ?? ta.value.length
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value').set
    nativeSetter.call(ta, ta.value.slice(0, start) + emoji + ta.value.slice(end))
    ta.dispatchEvent(new Event('input', { bubbles: true }))
    setTimeout(() => { ta.selectionStart = ta.selectionEnd = start + emoji.length; ta.focus() }, 0)
  }

  return (
    <div
      ref={pickerRef}
      className="absolute bottom-full mb-2 left-0 z-50 bg-surface-container border-2 border-on-background neo-shadow-sm w-72 p-3 select-none"
      onMouseDown={e => e.preventDefault()}
    >
      <div className="flex gap-1 mb-2 border-b-2 border-on-background/10 pb-2 overflow-x-auto">
        {EMOJI_TABS.map((tab, i) => (
          <button
            key={i}
            onMouseDown={e => { e.preventDefault(); setActiveTab(i) }}
            className={`shrink-0 text-base px-2 py-1 transition-colors ${activeTab === i ? 'bg-primary-container text-on-primary-fixed' : 'hover:bg-surface-container-high'}`}
          >{tab.label}</button>
        ))}
      </div>
      <div className="grid grid-cols-8 gap-0.5 max-h-40 overflow-y-auto">
        {EMOJI_TABS[activeTab].emojis.map((em, i) => (
          <button
            key={i}
            onMouseDown={e => { e.preventDefault(); insertEmoji(em) }}
            className="text-lg p-1 hover:bg-primary-container/30 transition-colors leading-none"
          >{em}</button>
        ))}
      </div>
    </div>
  )
}

// ── Reply input ───────────────────────────────────────────────
function ReplyInput({ parentName, onSubmit, onCancel }) {
  const [text, setText] = useState('')
  const [showEmoji, setShowEmoji] = useState(false)
  const ref = useRef(null)
  const { user } = useAuth()

  const submit = () => {
    if (!text.trim()) return
    onSubmit(text)
    setText('')
  }

  return (
    <div className="flex gap-2 items-start mt-2">
      <img src={user?.avatar || "/favicon.jpg"} alt="me" className="w-7 h-7 shrink-0 border border-on-background/20 object-cover mt-1" />
      <div className="flex-1 relative">
        <textarea
          ref={ref}
          value={text}
          onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submit() } }}
          placeholder={`Reply to ${parentName}…`}
          rows={2}
          className="w-full bg-background border border-on-background/20 focus:border-primary-container focus:outline-none px-3 py-2 text-sm resize-none text-on-surface placeholder:text-on-surface-variant"
        />
        <div className="flex justify-between items-center mt-1">
          <div className="relative">
            <button onClick={() => setShowEmoji(v => !v)} className="hover:text-primary-container transition-colors text-on-surface-variant">
              <Icon name="sentiment_satisfied" className="text-[18px]" />
            </button>
            {showEmoji && <EmojiPicker textareaRef={ref} onClose={() => setShowEmoji(false)} />}
          </div>
          <div className="flex gap-2">
            <button onClick={onCancel} className="text-[11px] text-on-surface-variant hover:underline px-2 py-1">Cancel</button>
            <button
              onClick={submit}
              disabled={!text.trim()}
              className="px-3 py-1 text-[11px] font-bold bg-primary-container text-on-primary-fixed border border-on-background/20 disabled:opacity-40 hover:brightness-105 active:scale-95 transition-all"
            >Reply</button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Single comment row (flat — no recursion) ──────────────────
function CommentRow({ comment, onReply, onUpdate, onDelete }) {
  const [liked, setLiked] = useState(comment.isLiked ?? comment.likedByMe ?? comment.liked ?? false)
  const [likeCount, setLikeCount] = useState(comment.likes ?? comment.likesCount ?? 0)
  const [showReply, setShowReply] = useState(false)
  const isLikedNow = liked || comment.isLiked || comment.likedByMe || comment.liked
  const [menuOpen, setMenuOpen] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(comment.content)
  const menuRef = useRef(null)
  const menuButtonRef = useRef(null)
  const navigate = useNavigate()
  const { user: authUser, isAdmin } = useAuth()
  const toast = useToast()
  const [adminDeleting, setAdminDeleting] = useState(false)
  const [adminDeleted, setAdminDeleted] = useState(false)

  const currentUserIds = new Set([authUser?.id, authUser?.profileId, authUser?.userId, authUser?._id].filter(Boolean))
  const isOwner = [comment.author?.id, comment.author?.userId, comment.author?._id].some((id) => currentUserIds.has(id))

  useEffect(() => {
    if (!menuOpen) return
    const handlePointerDown = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    setDraft(comment.content)
  }, [comment.content])

  const goToProfile = () => {
    if (!comment.author?.id) return
    if (comment.author.id === authUser?.id) navigate('/profile')
    else navigate(`/profile/${comment.author.id}`)
  }

  const handleLike = async () => {
    if (isOwner) return
    const prevLiked = liked
    const prevCount = likeCount
    setLiked(!prevLiked)
    setLikeCount(prevLiked ? prevCount - 1 : prevCount + 1)
    try {
      await apiLikeComment(comment.id)
    } catch {
      setLiked(prevLiked)
      setLikeCount(prevCount)
    }
  }

  const submitEdit = async () => {
    const next = draft.trim()
    if (!next || next === comment.content) {
      setIsEditing(false)
      setDraft(comment.content)
      return
    }
    try {
      await onUpdate(comment.id, next)
      setIsEditing(false)
    } catch {
      toast.error('Could not update comment.')
    }
  }

  const handleDelete = async () => {
    setMenuOpen(false)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    setShowDeleteConfirm(false)
    if (isAdmin && !isOwner) {
      setAdminDeleting(true)
      try {
        await adminDeleteComment(comment.id)
        setAdminDeleted(true)
        toast.success('Comment removed.')
      } catch {
        toast.error('Could not remove comment.')
      } finally {
        setAdminDeleting(false)
      }
      return
    }
    try {
      await onDelete(comment.id)
    } catch {
      toast.error('Could not delete comment.')
    }
  }

  const copyCommentId = async () => {
    setMenuOpen(false)
    try {
      await navigator.clipboard.writeText(comment.id)
      toast.success('Comment ID copied — paste it into Remove Content in the admin panel.')
    } catch {
      toast.error('Could not copy comment ID.')
    }
  }

  const handleMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const items = menuRef.current?.querySelectorAll('[role="menuitem"]')
    if (!items || items.length === 0) return
    const currentIndex = Array.from(items).findIndex((item) => item === document.activeElement)
    const nextIndex = event.key === 'ArrowDown'
      ? (currentIndex + 1) % items.length
      : (currentIndex <= 0 ? items.length - 1 : currentIndex - 1)
    items[nextIndex]?.focus()
  }

  if (adminDeleted) return null

  return (
    <>
      <div className="flex gap-3">
        <img
          src={comment.author?.avatar || '/favicon.jpg'}
          alt={comment.author?.name || 'User'}
          onClick={goToProfile}
          className="w-9 h-9 shrink-0 border border-on-background/20 object-cover cursor-pointer hover:opacity-80 transition-opacity"
        />
        <div className="flex-1 min-w-0">
          {/* Bubble */}
          <div className="bg-surface-container px-3 py-2.5 border border-on-background/10 relative">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span onClick={goToProfile} className="font-bold text-sm text-on-background cursor-pointer hover:underline">{comment.author?.name || 'Anonymous'}</span>
                  <span className="text-[10px] text-on-surface-variant font-mono">{comment.author?.handle || ''}</span>
                </div>
                {isEditing ? (
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={3}
                    className="w-full mt-2 bg-background border border-on-background/20 focus:border-primary-container focus:outline-none px-3 py-2 text-sm resize-none text-on-surface"
                  />
                ) : (
                  <p className="text-sm text-on-background mt-1 leading-relaxed break-words">{renderTextContent(comment.content)}</p>
                )}
              </div>
              {(isOwner || isAdmin) && !isEditing && (
                <div className="relative shrink-0" ref={menuRef}>
                  <button
                    ref={menuButtonRef}
                    type="button"
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="Open comment options"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-on-background/15 bg-surface-container hover:bg-primary-container/10 focus:outline-none focus:ring-2 focus:ring-primary-container"
                  >
                    <Icon name="more_horiz" className="text-[18px] text-on-background" />
                  </button>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-40 bg-black/10 md:hidden" onClick={() => setMenuOpen(false)} />
                      <div
                        role="menu"
                        aria-label="Comment actions"
                        tabIndex={-1}
                        onKeyDown={handleMenuKeyDown}
                        className="fixed inset-x-0 bottom-0 z-50 md:hidden bg-surface-container border-t border-on-background/15 p-2"
                      >
                        <div className="mx-auto max-w-md rounded-2xl bg-surface-container">
                          {isOwner && (
                            <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setIsEditing(true) }} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10">
                              <Icon name="edit" className="text-[18px]" />
                              <span>Edit Comment</span>
                            </button>
                          )}
                          <button type="button" role="menuitem" onClick={copyCommentId} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10">
                            <Icon name="content_copy" className="text-[18px]" />
                            <span>Copy Comment ID</span>
                          </button>
                          <button type="button" role="menuitem" onClick={handleDelete} disabled={adminDeleting} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-error hover:bg-error/10">
                            <Icon name="delete" className="text-[18px]" />
                            <span>Delete Comment</span>
                          </button>
                        </div>
                      </div>
                      <div className="absolute right-0 top-full z-50 mt-2 hidden min-w-48 rounded-xl border border-on-background/15 bg-surface-container shadow-xl md:block">
                        {isOwner && (
                          <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setIsEditing(true) }} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10 focus:outline-none focus:bg-primary-container/10">
                            <Icon name="edit" className="text-[18px]" />
                            <span>Edit Comment</span>
                          </button>
                        )}
                        <button type="button" role="menuitem" onClick={copyCommentId} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10 focus:outline-none focus:bg-primary-container/10">
                          <Icon name="content_copy" className="text-[18px]" />
                          <span>Copy Comment ID</span>
                        </button>
                        <button type="button" role="menuitem" onClick={handleDelete} disabled={adminDeleting} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-error hover:bg-error/10 focus:outline-none focus:bg-error/10">
                          <Icon name="delete" className="text-[18px]" />
                          <span>Delete Comment</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {isEditing && (
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => { setIsEditing(false); setDraft(comment.content) }} className="px-3 py-1.5 text-[11px] text-on-surface-variant hover:underline">Cancel</button>
                <button onClick={submitEdit} disabled={!draft.trim()} className="px-3 py-1.5 text-[11px] font-bold bg-primary-container text-on-primary-fixed border border-on-background/20 disabled:opacity-40">Save</button>
              </div>
            )}
          </div>
          {/* Actions */}
          <div className="flex items-center gap-4 mt-1.5 px-1">
            <button
              onClick={handleLike}
              disabled={isOwner}
              title={isOwner ? "You can't like your own comment" : undefined}
              className={`flex items-center gap-1 text-[11px] font-bold transition-colors ${isOwner ? 'text-on-surface-variant/40 cursor-not-allowed' : isLikedNow ? 'text-error' : 'text-on-surface-variant hover:text-error'}`}
            >
              <Icon name="favorite" filled={isLikedNow} className="text-[14px]" />
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            <button
              onClick={() => setShowReply(v => !v)}
              className="text-[11px] font-bold text-on-surface-variant hover:text-primary-container transition-colors"
            >Reply</button>
            <span className="text-[10px] text-on-surface-variant font-mono ml-auto">{comment.timestamp}</span>
          </div>

          {/* Inline reply input */}
          {showReply && (
            <ReplyInput
              parentName={comment.author?.name || 'User'}
              onSubmit={(text) => {
                onReply(comment.id, text)
                setShowReply(false)
              }}
              onCancel={() => setShowReply(false)}
            />
          )}
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-on-background/15 bg-surface-container p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-bold text-on-background">Delete comment?</p>
            <p className="mt-1 text-sm text-on-surface-variant">This action can’t be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium text-on-background hover:bg-primary-container/10">Cancel</button>
              <button type="button" onClick={confirmDelete} className="px-4 py-2 text-sm font-bold bg-error text-on-error hover:brightness-95">Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Reply row (indented, no further nesting) ──────────────────
function ReplyRow({ reply, onUpdate, onDelete }) {
  const [liked, setLiked] = useState(reply.isLiked ?? reply.likedByMe ?? reply.liked ?? false)
  const [likeCount, setLikeCount] = useState(reply.likes ?? reply.likesCount ?? 0)
  const [menuOpen, setMenuOpen] = useState(false)
  const isLikedNow = liked || reply.isLiked || reply.likedByMe || reply.liked
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [draft, setDraft] = useState(reply.content)
  const menuRef = useRef(null)
  const menuButtonRef = useRef(null)
  const navigate = useNavigate()
  const { user: authUser, isAdmin } = useAuth()
  const [adminDeleting, setAdminDeleting] = useState(false)
  const [adminDeleted, setAdminDeleted] = useState(false)
  const toast = useToast()

  const currentUserIds = new Set([authUser?.id, authUser?.profileId, authUser?.userId, authUser?._id].filter(Boolean))
  const isOwner = [reply.author?.id, reply.author?.userId, reply.author?._id].some((id) => currentUserIds.has(id))

  useEffect(() => {
    if (!menuOpen) return
    const handlePointerDown = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        menuButtonRef.current &&
        !menuButtonRef.current.contains(event.target)
      ) {
        setMenuOpen(false)
      }
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMenuOpen(false)
        menuButtonRef.current?.focus()
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('mousedown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [menuOpen])

  useEffect(() => {
    setDraft(reply.content)
  }, [reply.content])

  const goToProfile = () => {
    if (!reply.author?.id) return
    if (reply.author.id === authUser?.id) navigate('/profile')
    else navigate(`/profile/${reply.author.id}`)
  }

  const handleLike = async () => {
    if (isOwner) return
    const prevLiked = liked
    const prevCount = likeCount
    setLiked(!prevLiked)
    setLikeCount(prevLiked ? prevCount - 1 : prevCount + 1)
    try {
      await apiLikeComment(reply.id)
    } catch {
      setLiked(prevLiked)
      setLikeCount(prevCount)
    }
  }

  const submitEdit = async () => {
    const next = draft.trim()
    if (!next || next === reply.content) {
      setIsEditing(false)
      setDraft(reply.content)
      return
    }
    try {
      await onUpdate(reply.id, next)
      setIsEditing(false)
    } catch {
      toast.error('Could not update comment.')
    }
  }

  const handleDelete = async () => {
    setMenuOpen(false)
    setShowDeleteConfirm(true)
  }

  const confirmDelete = async () => {
    setShowDeleteConfirm(false)
    if (isAdmin && !isOwner) {
      setAdminDeleting(true)
      try {
        await adminDeleteComment(reply.id)
        setAdminDeleted(true)
        toast.success('Reply removed.')
      } catch {
        toast.error('Could not remove reply.')
      } finally {
        setAdminDeleting(false)
      }
      return
    }
    try {
      await onDelete(reply.id)
    } catch {
      toast.error('Could not delete comment.')
    }
  }

  const copyReplyId = async () => {
    setMenuOpen(false)
    try {
      await navigator.clipboard.writeText(reply.id)
      toast.success('Reply ID copied — paste it into Remove Content in the admin panel.')
    } catch {
      toast.error('Could not copy reply ID.')
    }
  }

  const handleMenuKeyDown = (event) => {
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
    event.preventDefault()
    const items = menuRef.current?.querySelectorAll('[role="menuitem"]')
    if (!items || items.length === 0) return
    const currentIndex = Array.from(items).findIndex((item) => item === document.activeElement)
    const nextIndex = event.key === 'ArrowDown'
      ? (currentIndex + 1) % items.length
      : (currentIndex <= 0 ? items.length - 1 : currentIndex - 1)
    items[nextIndex]?.focus()
  }

  if (adminDeleted) return null

  return (
    <>
      <div className="flex gap-3 ml-12 pl-3 border-l-2 border-primary-container/30">
        <img
          src={reply.author?.avatar || '/favicon.jpg'}
          alt={reply.author?.name || 'User'}
          onClick={goToProfile}
          className="w-7 h-7 shrink-0 border border-on-background/20 object-cover cursor-pointer hover:opacity-80 transition-opacity"
        />
        <div className="flex-1 min-w-0">
          <div className="bg-surface-container/60 px-3 py-2 border border-on-background/10 relative">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span onClick={goToProfile} className="font-bold text-xs text-on-background cursor-pointer hover:underline">{reply.author?.name || 'Anonymous'}</span>
                  <span className="text-[10px] text-on-surface-variant font-mono">{reply.author?.handle || ''}</span>
                </div>
                {isEditing ? (
                  <textarea
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    rows={2}
                    className="w-full mt-2 bg-background border border-on-background/20 focus:border-primary-container focus:outline-none px-3 py-2 text-sm resize-none text-on-surface"
                  />
                ) : (
                  <p className="text-sm text-on-background mt-1 leading-relaxed break-words">{renderTextContent(reply.content)}</p>
                )}
              </div>
              {(isOwner || isAdmin) && !isEditing && (
                <div className="relative shrink-0" ref={menuRef}>
                  <button
                    ref={menuButtonRef}
                    type="button"
                    onClick={() => setMenuOpen((v) => !v)}
                    aria-label="Open reply options"
                    aria-haspopup="menu"
                    aria-expanded={menuOpen}
                    className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-on-background/15 bg-surface-container hover:bg-primary-container/10 focus:outline-none focus:ring-2 focus:ring-primary-container"
                  >
                    <Icon name="more_horiz" className="text-[16px] text-on-background" />
                  </button>
                  {menuOpen && (
                    <>
                      <div className="fixed inset-0 z-40 bg-black/10 md:hidden" onClick={() => setMenuOpen(false)} />
                      <div
                        role="menu"
                        aria-label="Reply actions"
                        tabIndex={-1}
                        onKeyDown={handleMenuKeyDown}
                        className="fixed inset-x-0 bottom-0 z-50 md:hidden bg-surface-container border-t border-on-background/15 p-2"
                      >
                        <div className="mx-auto max-w-md rounded-2xl bg-surface-container">
                          {isOwner && (
                            <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setIsEditing(true) }} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10">
                              <Icon name="edit" className="text-[18px]" />
                              <span>Edit Reply</span>
                            </button>
                          )}
                          <button type="button" role="menuitem" onClick={copyReplyId} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10">
                            <Icon name="content_copy" className="text-[18px]" />
                            <span>Copy Reply ID</span>
                          </button>
                          <button type="button" role="menuitem" onClick={handleDelete} disabled={adminDeleting} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-error hover:bg-error/10">
                            <Icon name="delete" className="text-[18px]" />
                            <span>Delete Reply</span>
                          </button>
                        </div>
                      </div>
                      <div className="absolute right-0 top-full z-50 mt-2 hidden min-w-48 rounded-xl border border-on-background/15 bg-surface-container shadow-xl md:block">
                        {isOwner && (
                          <button type="button" role="menuitem" onClick={() => { setMenuOpen(false); setIsEditing(true) }} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10 focus:outline-none focus:bg-primary-container/10">
                            <Icon name="edit" className="text-[18px]" />
                            <span>Edit Reply</span>
                          </button>
                        )}
                        <button type="button" role="menuitem" onClick={copyReplyId} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-on-background hover:bg-primary-container/10 focus:outline-none focus:bg-primary-container/10">
                          <Icon name="content_copy" className="text-[18px]" />
                          <span>Copy Reply ID</span>
                        </button>
                        <button type="button" role="menuitem" onClick={handleDelete} disabled={adminDeleting} className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm font-medium text-error hover:bg-error/10 focus:outline-none focus:bg-error/10">
                          <Icon name="delete" className="text-[18px]" />
                          <span>Delete Reply</span>
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
            {isEditing && (
              <div className="flex justify-end gap-2 mt-2">
                <button onClick={() => { setIsEditing(false); setDraft(reply.content) }} className="px-3 py-1.5 text-[11px] text-on-surface-variant hover:underline">Cancel</button>
                <button onClick={submitEdit} disabled={!draft.trim()} className="px-3 py-1.5 text-[11px] font-bold bg-primary-container text-on-primary-fixed border border-on-background/20 disabled:opacity-40">Save</button>
              </div>
            )}
          </div>
          <div className="flex items-center gap-4 mt-1 px-1">
            <button
              onClick={handleLike}
              disabled={isOwner}
              title={isOwner ? "You can't like your own reply" : undefined}
              className={`flex items-center gap-1 text-[11px] font-bold transition-colors ${isOwner ? 'text-on-surface-variant/40 cursor-not-allowed' : isLikedNow ? 'text-error' : 'text-on-surface-variant hover:text-error'}`}
            >
              <Icon name="favorite" filled={isLikedNow} className="text-[14px]" />
              {likeCount > 0 && <span>{likeCount}</span>}
            </button>
            <span className="text-[10px] text-on-surface-variant font-mono ml-auto">{reply.timestamp}</span>
          </div>
        </div>
      </div>
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(false)}>
          <div className="w-full max-w-sm rounded-2xl border border-on-background/15 bg-surface-container p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <p className="text-base font-bold text-on-background">Delete reply?</p>
            <p className="mt-1 text-sm text-on-surface-variant">This action can’t be undone.</p>
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={() => setShowDeleteConfirm(false)} className="px-4 py-2 text-sm font-medium text-on-background hover:bg-primary-container/10">Cancel</button>
              <button type="button" onClick={confirmDelete} className="px-4 py-2 text-sm font-bold bg-error text-on-error hover:brightness-95">Delete</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

// ── Comment thread (comment + its flat replies) ───────────────
function CommentThread({ comment, onReply, onUpdate, onDelete }) {
  return (
    <div className="flex flex-col gap-3">
      <CommentRow comment={comment} onReply={onReply} onUpdate={onUpdate} onDelete={onDelete} />
      {comment.replies?.length > 0 && (
        <div className="flex flex-col gap-3">
          {comment.replies.map(reply => (
            <ReplyRow key={`${reply.id}-${reply.isLiked}`} reply={reply} onUpdate={onUpdate} onDelete={onDelete} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────
export default function CommentsPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const toast = useToast()

  // Post data is passed via router state from PostCard
  const post = location.state?.post
  const commentPostId = post?.interactionPostId ?? post?.id

  const [comments, setComments] = useState([])
  const [commentCount, setCommentCount] = useState(post?.comments ?? 0)
  const [newComment, setNewComment] = useState('')
  const [showEmoji, setShowEmoji] = useState(false)
  const inputRef = useRef(null)

  // Frontend-only cooldown between comment submissions (45s). This does not
  // replace server-side rate limiting — it only prevents rapid clicks/
  // accidental double-posts from this client; a user could bypass it by
  // calling the API directly unless the backend also enforces a limit.
  const COMMENT_COOLDOWN_SECONDS = 45
  const [cooldownRemaining, setCooldownRemaining] = useState(0)

  useEffect(() => {
    if (cooldownRemaining <= 0) return
    const id = setInterval(() => {
      setCooldownRemaining(s => (s <= 1 ? 0 : s - 1))
    }, 1000)
    return () => clearInterval(id)
  }, [cooldownRemaining])

  const [commentsLoading, setCommentsLoading] = useState(false)
  const [commentsError, setCommentsError]     = useState(null)

  // Fetch real comments on mount
  useEffect(() => {
    window.scrollTo(0, 0)
    if (commentPostId) {
      setCommentsLoading(true)
      setCommentsError(null)
      getComments(commentPostId)
        .then(async data => {
          const topLevel = Array.isArray(data) ? data : (data.data ?? data.comments ?? [])
          // For each comment with replies, fetch them via parentCommentId param
          const withReplies = await Promise.all(topLevel.map(async c => {
            if ((c.replyCount ?? c.commentCount ?? 0) === 0) return c
            try {
              const replyData = await getComments(commentPostId, c.id)
              const replies = Array.isArray(replyData) ? replyData : (replyData.data ?? [])
              return { ...c, replies }
            } catch { return c }
          }))
          setComments(withReplies)
        })
        .catch(err => setCommentsError(err.message || 'Failed to load comments'))
        .finally(() => setCommentsLoading(false))
    }
  }, [commentPostId])

  if (!post) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 text-on-surface-variant">
        <p className="font-bold text-sm uppercase tracking-widest">Post not found</p>
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-primary-container font-bold text-sm hover:underline">
          <Icon name="arrow_back" className="text-[18px]" /> Go back
        </button>
      </div>
    )
  }

  const submitComment = async () => {
    if (!newComment.trim() || cooldownRemaining > 0) return
    const optimistic = {
      id: `c_${Date.now()}`,
      author: {
        id: user?.id || '',
        name: user?.name || user?.fullName || 'You',
        handle: user?.handle || (user?.username ? `@${user.username}` : ''),
        avatar: user?.avatar || user?.avatarUrl  || '/favicon.jpg',
      },
      content: newComment,
      likes: 0,
      replies: [],
      timestamp: 'just now',
    }
    setComments(prev => [optimistic, ...prev])
    setCommentCount(n => n + 1)
    setNewComment('')
    setCooldownRemaining(COMMENT_COOLDOWN_SECONDS)
    try {
      const saved = await apiCreateComment(commentPostId, newComment)
      setComments(prev => prev.map(c => c.id === optimistic.id ? normalizeComment(saved?.data ?? saved) : c))
    } catch (err) {
      // Backend rejected the comment (e.g. anti-farming checks) — remove the
      // optimistic comment, refund the cooldown, and tell the user why.
      setComments(prev => prev.filter(c => c.id !== optimistic.id))
      setCommentCount(n => Math.max(0, n - 1))
      setCooldownRemaining(0)
      toast.error(humanizeApiError(err))
    }
  }

  const handleReply = async (commentId, text) => {
    const optimistic = {
      id: `r_${Date.now()}`,
      author: {
        id: user?.id || '',
        name: user?.name || user?.fullName || 'You',
        handle: user?.handle || (user?.username ? `@${user.username}` : ''),
        avatar: user?.avatar || user?.avatarUrl  || '/favicon.jpg',
      },
      content: text,
      likes: 0,
      timestamp: 'just now',
    }
    setComments(prev => prev.map(c =>
      c.id === commentId
        ? { ...c, replies: [...(c.replies ?? []), optimistic] }
        : c
    ))
    try {
      const saved = await apiCreateReply(commentPostId, commentId, text)
      // Replace optimistic reply with real one from server
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, replies: c.replies.map(r => r.id === optimistic.id ? normalizeComment(saved?.data ?? saved) : r) }
          : c
      ))
    } catch (err) {
      // Remove the failed optimistic reply and tell the user why it didn't post.
      setComments(prev => prev.map(c =>
        c.id === commentId
          ? { ...c, replies: c.replies.filter(r => r.id !== optimistic.id) }
          : c
      ))
      toast.error(humanizeApiError(err))
    }
  }

  const handleUpdateComment = async (commentId, nextContent) => {
    const prevComments = comments
    setComments(prev => updateCommentTree(prev, commentId, (item) => ({
      ...item,
      content: nextContent,
    })))
    try {
      await apiUpdateComment(commentId, nextContent)
    } catch (err) {
      setComments(prevComments)
      toast.error(humanizeApiError(err))
      throw new Error('Update failed')
    }
  }

  const handleDeleteComment = async (commentId) => {
    const prevComments = comments
    const prevCount = commentCount
    setComments(prev => removeCommentFromTree(prev, commentId))
    setCommentCount((n) => Math.max(0, n - 1))
    try {
      await apiDeleteComment(commentId)
    } catch {
      setComments(prevComments)
      setCommentCount(prevCount)
      toast.error('Could not delete comment.')
      throw new Error('Delete failed')
    }
  }

  return (
    <div className="min-h-screen bg-background text-on-background">

      {/* ── Sticky header ── */}
      <header className="sticky top-0 z-40 bg-background border-b-2 border-on-background/10 px-4 py-3 flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 flex items-center justify-center border-2 border-on-background/20 hover:border-primary-container hover:bg-primary-container/10 transition-all active:scale-95"
          aria-label="Go back"
        >
          <Icon name="arrow_back" className="text-[20px] text-on-background" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="font-bold text-sm uppercase tracking-widest text-on-background truncate">Comments</h1>
          <p className="text-[11px] text-on-surface-variant font-mono">{commentCount} comment{commentCount !== 1 ? 's' : ''}</p>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 lg:px-0 pb-32">

        {/* ── Post preview (compact) ── */}
        <div className="mt-4 mb-6 bg-surface-container border border-on-background/10 neo-border neo-shadow p-4">
          <div className="flex gap-3 items-start">
            <img
              src={post.author.avatar}
              alt={post.author.name}
              onClick={() => {
                if (!post.author?.id) return
                if (post.author.id === user?.id) navigate('/profile')
                else navigate(`/profile/${post.author.id}`)
              }}
              className="w-10 h-10 shrink-0 border border-on-background/20 object-cover cursor-pointer hover:opacity-80 transition-opacity"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span
                  onClick={() => {
                    if (!post.author?.id) return
                    if (post.author.id === user?.id) navigate('/profile')
                    else navigate(`/profile/${post.author.id}`)
                  }}
                  className="font-bold text-sm text-on-background cursor-pointer hover:underline"
                >{post.author.name}</span>
                <span className="text-[10px] text-on-surface-variant font-mono">{post.author.handle}</span>
                <span className="text-[10px] text-on-surface-variant font-mono ml-auto">{post.timestamp}</span>
              </div>
              <p className="text-sm text-on-background mt-1.5 leading-relaxed line-clamp-3">{post.content}</p>
              {/* Like / comment counts */}
              <div className="flex items-center gap-4 mt-2 pt-2 border-t border-on-background/10">
                <span className="flex items-center gap-1 text-[11px] text-on-surface-variant">
                  <Icon name="favorite" className="text-[14px]" />
                  {post.likes >= 1000 ? `${(post.likes/1000).toFixed(1)}k` : post.likes}
                </span>
                <span className="flex items-center gap-1 text-[11px] text-primary-container font-bold">
                  <Icon name="chat_bubble" className="text-[14px]" />
                  {commentCount}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Write a comment ── */}
        <div className="mb-6 bg-surface-container border border-on-background/10 neo-border p-4">
          <p className="font-bold text-[11px] uppercase tracking-widest text-on-surface-variant mb-3">Write a comment</p>
          <div className="flex gap-3">
            <img src={user?.avatar || "/favicon.jpg"} alt="me" className="w-9 h-9 shrink-0 border border-on-background/20 object-cover" />
            <div className="flex-1 relative">
              <textarea
                ref={inputRef}
                value={newComment}
                onChange={e => setNewComment(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); submitComment() } }}
                placeholder="Share your thoughts…"
                rows={3}
                className="w-full bg-background border border-on-background/20 focus:border-primary-container focus:outline-none px-3 py-2.5 text-sm resize-none text-on-surface placeholder:text-on-surface-variant"
              />
              <div className="flex justify-between items-center mt-2">
                <div className="relative">
                  <button
                    onClick={() => setShowEmoji(v => !v)}
                    className="hover:text-primary-container transition-colors text-on-surface-variant"
                  >
                    <Icon name="sentiment_satisfied" className="text-[20px]" />
                  </button>
                  {showEmoji && <EmojiPicker textareaRef={inputRef} onClose={() => setShowEmoji(false)} />}
                </div>
                <div className="flex items-center gap-3">
                  {cooldownRemaining > 0 && (
                    <span className="flex items-center gap-1 text-[11px] font-mono text-on-surface-variant tabular-nums">
                      <Icon name="timer" className="text-[16px]" />
                      {cooldownRemaining}s
                    </span>
                  )}
                  <button
                    onClick={submitComment}
                    disabled={!newComment.trim() || cooldownRemaining > 0}
                    className="px-5 py-2 text-sm font-bold bg-primary-container text-on-primary-fixed border border-on-background/20 neo-border disabled:opacity-40 hover:brightness-105 active:scale-95 transition-all"
                  >
                    {cooldownRemaining > 0 ? `Wait ${cooldownRemaining}s` : 'Comment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Comments list ── */}
        {commentsLoading ? (
          <div className="flex justify-center py-12">
            <div className="w-8 h-8 border-[3px] border-on-background border-t-transparent animate-spin" />
          </div>
        ) : commentsError ? (
          <div className="flex flex-col items-center gap-3 py-16 text-on-surface-variant">
            <Icon name="wifi_off" className="text-[40px] opacity-30" />
            <p className="text-sm font-bold uppercase tracking-widest opacity-50">Failed to load comments</p>
          </div>
        ) : comments.length > 0 ? (
          <div className="flex flex-col gap-6">
            {comments.map(comment => (
              <CommentThread
                key={`${comment.id}-${comment.isLiked}`}
                comment={comment}
                onReply={handleReply}
                onUpdate={handleUpdateComment}
                onDelete={handleDeleteComment}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3 py-16 text-on-surface-variant">
            <Icon name="chat_bubble_outline" className="text-[40px] opacity-30" />
            <p className="text-sm font-bold uppercase tracking-widest opacity-50">No comments yet</p>
            <p className="text-xs opacity-40">Be the first to share your thoughts</p>
          </div>
        )}

        {/* Bottom spacer for mobile nav */}
        <div className="h-8" />
      </div>
    </div>
  )
}